// src/auth/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import apiClient from '../services/apiClient'; // <-- Usar apiClient para las llamadas
import { tienePermiso } from './roles';
import { limpiarFiltrosPersistentes } from '../hooks/useFiltroPersistente';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    // Función centralizada para manejar el token
    const handleToken = (newToken) => {
        localStorage.setItem('token', newToken);
        const decodedToken = jwtDecode(newToken);
        // Guardamos el nombre del usuario desde el token
        setUser({
            id: decodedToken.id,
            email: decodedToken.sub,
            rol: decodedToken.rol,
            nombre: decodedToken.nombre
        });
        setToken(newToken);
        setLoading(false);
        // Un login no recarga la página (a diferencia del logout, ver abajo): si alguien
        // deslogueó a un usuario y entró con otro en la misma pestaña, los filtros de listados
        // que hubiera tipeado el anterior no tienen que quedar pegados.
        limpiarFiltrosPersistentes();
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        limpiarFiltrosPersistentes();
        window.location.href = '/login'; // Redirigir al login
    };

    useEffect(() => {
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    // Aseguramos que el nombre se cargue al refrescar la página
                    setUser({
                        id: decodedToken.id,
                        email: decodedToken.sub,
                        rol: decodedToken.rol,
                        nombre: decodedToken.nombre
                    });
                }
            } catch (error) {
                console.error("Token inválido:", error);
                logout();
            }
        }
        setLoading(false);
    }, [token]);

    // El JWT no lleva la foto de perfil (pesa): se pide aparte una vez conocido el usuario.
    const idUsuario = user?.id;
    useEffect(() => {
        if (!idUsuario) return undefined;
        let cancelado = false;
        apiClient.get('/usuarios/me')
            .then((r) => {
                if (!cancelado) setUser((prev) => (prev && prev.id === idUsuario ? { ...prev, fotoPerfil: r.data?.fotoPerfil || null } : prev));
            })
            .catch(() => { /* sin foto: se muestra el avatar por defecto */ });
        return () => { cancelado = true; };
    }, [idUsuario]);

    const login = async (email, password) => {
        // Usamos apiClient que ya tiene la baseURL
        const response = await apiClient.post('/auth/login', { email, password });
        handleToken(response.data.token);
    };

    const register = async (nombre, email, password) => {
        // Usamos apiClient para el registro
        const response = await apiClient.post('/auth/register', { nombre, email, password });
        // NO iniciamos sesión automáticamente - el usuario debe esperar validación del admin
        // Devolvemos la respuesta para que RegisterPage pueda mostrar el mensaje
        return response.data;
    };

    const puede = (permiso) => tienePermiso(user?.rol, permiso);
    const esRol = (...roles) => !!user && roles.includes(user.rol);

    // Actualiza el usuario en memoria sin re-loguear. Necesario porque el backend
    // de "actualizar perfil" no emite un JWT nuevo (el nombre/email quedan guardados
    // en BD pero el token viejo, y por lo tanto `user`, no se entera solo).
    const actualizarUsuario = (datosParciales) => {
        setUser((prev) => (prev ? { ...prev, ...datosParciales } : prev));
    };

    const value = {
        user,
        token,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
        register,
        puede,
        esRol,
        actualizarUsuario,
    };

    if (loading) {
        return <div>Cargando...</div>;
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};
